import React, { memo, useCallback, useEffect, useMemo } from 'react'

import {
  Button, Checkbox, DualSlider, Icon, InfoOutlined, Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addPolygonRange, clearPolygon, removePolygonRange, setMaxPolygon, setMinPolygon, setPolygonRanges,
} from 'Pods/SearchPage/Store/actions/polygon'
import { POLYGON_RANGE } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

const MIN_POLYGONS = 0
const MAX_POLYGONS = 1_000_000

const QUICK_SELECTIONS = [
  { value: POLYGON_RANGE.LOW_POLY, key: 'low_poly' },
  { value: POLYGON_RANGE.MID_POLY, key: 'mid_poly' },
  { value: POLYGON_RANGE.HIGH_POLY, key: 'high_poly' },
]

function PolyCount() {
  const t = useTranslate()

  const polygonRanges = useSearchPageStore(state => state.polygonRanges)
  const minPolygon = useSearchPageStore(state => state.minPolygon)
  const maxPolygon = useSearchPageStore(state => state.maxPolygon)

  // PolyCount only mounts when assetType === THREE_D_ONLY (see SecondaryFilters),
  // so unmount means the user switched to a different asset type - drop the
  // polygon selection so it does not silently carry over.
  useEffect(() => () => clearPolygon(), [])

  const selectedSet = useMemo(() => new Set(polygonRanges), [polygonRanges])
  const isCustomRangeActive = minPolygon !== undefined || maxPolygon !== undefined
  const selectionCount = polygonRanges.length + (isCustomRangeActive ? 1 : 0)
  const hasSelectedAnyFilters = selectionCount > 0
  const placeholder = hasSelectedAnyFilters
    ? t('search_page.filters.poly_count.title_with_count', { count: selectionCount })
    : t('search_page.filters.poly_count.title')
  // Presets and the custom range are mutually exclusive, so the preset count is the widest label.
  const reservedPlaceholder = t('search_page.filters.poly_count.title_with_count', {
    count: QUICK_SELECTIONS.length,
  })

  // Per CU-86c74u3x2: multiple preset checkboxes may be active simultaneously,
  // but the preset group and the custom slider range are mutually exclusive -
  // activating the slider clears all presets, and activating any preset clears
  // the custom range. Within the preset group, checkboxes are additive.
  const handleSliderChange = useCallback(({ min, max }) => {
    const newMin = min === MIN_POLYGONS ? undefined : min
    const newMax = max === MAX_POLYGONS ? undefined : max
    setMinPolygon(newMin)
    setMaxPolygon(newMax)
    if (newMin !== undefined || newMax !== undefined) {
      setPolygonRanges([])
    }
  }, [])

  const handleCheckboxChange = useCallback((value, isChecked) => {
    if (isChecked) {
      addPolygonRange(value)
      setMinPolygon(undefined)
      setMaxPolygon(undefined)
    } else {
      removePolygonRange(value)
    }
  }, [])

  const handleReset = useCallback(() => {
    clearPolygon()
  }, [])

  const content = useMemo(() => (
    <div className="poly-count-filter">
      <div
        className="poly-count-filter__checkboxes"
        role="group"
        aria-label={t('search_page.filters.poly_count.checkboxes_group_label')}
      >
        {QUICK_SELECTIONS.map(({ value, key }, index) => (
          <Checkbox
            key={value}
            id={`poly-count-${value}`}
            name={`poly-count-${value}`}
            label={(
              <span className="poly-count-filter__label">
                {t(`search_page.filters.poly_count.${key}_label`)}
                <span className="poly-count-filter__additions">
                  {t(`search_page.filters.poly_count.${key}_value`)}
                </span>
              </span>
            )}
            isChecked={selectedSet.has(value)}
            isControlled
            trailLabel={index === 0 ? (
              <Tooltip
                content={t('search_page.filters.poly_count.info_tooltip')}
                place="bottom"
                tooltipClassName="poly-count-filter__tooltip"
              >
                <span
                  className="poly-count-filter__tooltip-trigger"
                  onClick={(event) => {
                    // The icon lives inside the Checkbox's <label>, so a native click
                    // would toggle the associated input. Prevent both defaults.
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                >
                  <Icon svgComponent={InfoOutlined} size="small" color="dark" notSpaced />
                </span>
              </Tooltip>
            ) : undefined}
            onChange={(isChecked) => handleCheckboxChange(value, isChecked)}
          />
        ))}
      </div>
      <DualSlider
        id="poly-count-slider"
        name="poly-count-slider"
        min={minPolygon ?? MIN_POLYGONS}
        max={maxPolygon ?? MAX_POLYGONS}
        minThreshold={MIN_POLYGONS}
        maxThreshold={MAX_POLYGONS}
        onChange={handleSliderChange}
        size="medium"
        withSliderOnTop
        withMarkers
        isShortenedValue
        withTextAlignCenter
      />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
        onClick={handleReset}
        disabled={!hasSelectedAnyFilters}
      >
        {t('search_page.filters.poly_count.reset')}
      </Button>
    </div>
  ), [
    selectedSet,
    minPolygon,
    maxPolygon,
    hasSelectedAnyFilters,
    handleCheckboxChange,
    handleSliderChange,
    handleReset,
  ])

  return (
    <FiltersWrapper
      placeholder={placeholder}
      reservedPlaceholder={reservedPlaceholder}
      content={content}
      hasSelectedAnyFilters={hasSelectedAnyFilters}
      closingDropdownClassName="filter-wrapper"
    />
  )
}

export default memo(PolyCount)
